import { css, html } from 'lit';
import { customElement, state, property, query } from 'lit/decorators.js';
import { TeComponent } from './common/te-component';
import { styleMap } from 'lit/directives/style-map';
import { theme } from './common/theme';

const longTouchDelayMs = 250;
const maxDistanceToMoveBeforeCanceling = 10;

const menuOpenCheckEvent = 'otherContextMenusOpenCheckingEvent';

/**
 * Context Menu Component
 *
 * @slot - Default slot for context menu items
 * @slot custom - Custom element from which context menu gets toggled
 */

@customElement('te-context-menu')
export class TeContextMenu extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-context-menu {
        margin: 0;
        padding: 0;
        position: relative;
        height: 100%;
        width: 100%;
        user-select: none;
      }

      .custom-element {
        width: 100%;
        height: 100%;
      }

      .te-context-menu-list {
        background: ${theme.colors.shade0};
        width: 184px;
        z-index: 10;
        position: absolute;
        top: 0;
        box-shadow: ${theme.shadows.secondary};
        border-radius: 4px;
        overflow: hidden;
        user-select: none;
      }
    `,
  ];

  /** Context menu opens with right click */
  @property({ type: Boolean }) rightClick = false;

  @state() onDocumentMouseupFn: (e: MouseEvent) => void;
  @state() onDocumentTouchendFn: (e: TouchEvent) => void;
  @state() onMenuOpenCheckFn: (e: Event) => void;
  @state() isMenuOpen = false;
  @state() width = 0;
  @state() height = 0;
  @state() timer?: number;
  @state() x = 0;
  @state() y = 0;
  @state() touchStartPos?: { x: number; y: number };
  @state() touchDevice = false;
  @state() normalClickOnRight = false;
  @state() movedTooFar = false;

  @query('.te-context-menu-list') _menuSection?: HTMLDivElement;

  constructor() {
    super();
    this.onDocumentMouseupFn = this.onNormalMouseup.bind(this);
    this.onMenuOpenCheckFn = this.onMenuOpenCheck.bind(this);
    this.onDocumentTouchendFn = this.onNormalTouchend.bind(this);
    document.addEventListener('mouseup', this.onDocumentMouseupFn);
    document.addEventListener('contextmenu', this.onDocumentMouseupFn);
    document.addEventListener('touchend', this.onDocumentTouchendFn);
    document.addEventListener(menuOpenCheckEvent, this.onMenuOpenCheckFn);
  }

  disconnectedCallback() {
    document.removeEventListener('mouseup', this.onDocumentMouseupFn);
    document.removeEventListener('contextmenu', this.onDocumentMouseupFn);
    document.removeEventListener(menuOpenCheckEvent, this.onMenuOpenCheckFn);
  }

  firstUpdated() {
    const showCase = this?.shadowRoot?.querySelector('te-context-menu');
    this.touchDevice = 'ontouchmove' in document.documentElement;

    if (showCase?.nodeName === 'TE-CONTEXT-MENU' || this?.nodeName === 'TE-CONTEXT-MENU') {
      const container = showCase || this;
      this.width = container.clientWidth;
      this.height = container.clientHeight;
    }
  }

  updated(changedProps: Map<string, unknown>) {
    const { _menuSection, x, y } = this;
    if ((changedProps.has('isMenuOpen') && this.isMenuOpen) || this.normalClickOnRight) {
      this.checkMenuOpenEvent();
      this.normalClickOnRight = false;
    }
    if (!_menuSection) return;
    const { left, right, top, bottom } = this.getBoundingClientRect();
    const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

    const menuHeight = _menuSection.offsetHeight + 4;
    const menuWidth = _menuSection.offsetWidth + 8;

    if (windowHeight - y < menuHeight) {
      _menuSection.style.bottom = `${Math.abs(bottom - y)}px`;
      _menuSection.style.top = 'unset';
    } else {
      _menuSection.style.top = `${Math.abs(top - y)}px`;
      _menuSection.style.bottom = 'unset';
    }

    if (windowWidth - x < menuWidth) {
      _menuSection.style.right = `${Math.abs(right - x) + 4}px`;
      _menuSection.style.left = 'unset';
    } else {
      _menuSection.style.left = `${Math.abs(left - x) + 4}px`;
      _menuSection.style.right = 'unset';
    }
  }

  onMenuOpenCheck(e: unknown) {
    if ((e as CustomEvent).detail !== this) {
      this.onMenuToggle(e as MouseEvent, false);
    }
  }

  isClickInsideElement(e: Event) {
    const target = e.target as Element;
    const showcase = target?.shadowRoot?.querySelector('.te-context-menu'); // For showcase element
    return this.contains(target) || showcase === this;
  }

  onContextMenu(e: Event) {
    if (this.rightClick) e.preventDefault();
  }

  getPosition(e: Event) {
    if (this.touchDevice && this.rightClick) {
      const [touch] = (e as TouchEvent).changedTouches;
      const { clientX, clientY } = touch;
      return {
        x: clientX,
        y: clientY,
      };
    }
    const mouseEvent = e as MouseEvent;
    return {
      x: mouseEvent.x || mouseEvent.clientX || 0,
      y: mouseEvent.y || mouseEvent.clientY || 0,
    };
  }

  positionMenu(e: Event) {
    const { x, y } = this.getPosition(e);
    this.x = x;
    this.y = y;
  }

  onMenuToggle(e: Event, val: boolean) {
    if (val) this.positionMenu(e);
    this.isMenuOpen = val;
  }

  onNormalMouseup(e: MouseEvent) {
    e.stopPropagation();
    if (!this.isClickInsideElement(e) || this.rightClick) {
      this.onMenuToggle(e, false);
    }
  }

  onNormalTouchend(e: TouchEvent) {
    e.stopPropagation();
    if (!this.isClickInsideElement(e)) {
      this.onMenuToggle(e, false);
    }
  }

  onMouseUp(e: MouseEvent) {
    e.stopPropagation();
    if (this.touchDevice && this.rightClick) {
      return;
    }
    const isClickInsideElement = this.isClickInsideElement(e);
    const shouldOpen = this.rightClick ? e.button === 2 : !this.isMenuOpen;
    this.normalClickOnRight = this.rightClick && e.button !== 2;
    this.onMenuToggle(e, isClickInsideElement && shouldOpen);
  }

  onTouchStart(e: TouchEvent) {
    e.stopPropagation();
    if (!this.rightClick) return;
    const [touch] = e.changedTouches;
    this.touchStartPos = {
      x: touch.screenX,
      y: touch.screenY,
    };
    if (this.rightClick) {
      this.onMenuToggle(e, false);
      this.timer = window.setTimeout(() => {
        this.onMenuToggle(e, true);
        this.touchStartPos = undefined;
      }, longTouchDelayMs);
    }
  }

  onTouchMove(e: TouchEvent) {
    const [touch] = e.changedTouches;
    if (this.touchStartPos) {
      const distance = Math.hypot(
        this.touchStartPos.x - touch.screenX,
        this.touchStartPos.y - touch.screenY
      );
      this.movedTooFar = distance > maxDistanceToMoveBeforeCanceling;
      if (this.movedTooFar) {
        this.clearTimer();
      }
    }
  }

  clearTimer() {
    window.clearTimeout(this.timer);
  }

  onTouchEnd() {
    this.clearTimer();
  }

  checkMenuOpenEvent() {
    this.dispatchEvent(
      new CustomEvent(menuOpenCheckEvent, {
        detail: this,
        bubbles: true,
        composed: true,
      })
    );
  }

  getMenuListStyle() {
    const { isMenuOpen } = this;
    return {
      display: isMenuOpen ? 'block' : 'none',
    };
  }

  render() {
    return html`
      <div class="te-context-menu">
        <div class="te-context-menu-list" style=${styleMap(this.getMenuListStyle())}>
          <slot></slot>
        </div>
        <div
          @mouseup=${this.onMouseUp}
          @contextmenu=${this.onContextMenu}
          @touchstart=${this.onTouchStart}
          @touchend=${this.onTouchEnd}
          @touchmove=${this.onTouchMove}
          class="custom-element"
        >
          <slot name="custom"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-context-menu': TeContextMenu;
  }
}
